/**
 * Authentication type definitions
 */

import type { AccountInfo } from "@azure/msal-node";
import type { PowerPlatformClaimsBinding } from "./claims-challenge-store.js";

/**
 * Authentication provider interface
 */
export interface AuthProvider {
  /**
   * Fixed PPAPI /.default for the existing selected account and exact expected tenant.
   * Verify returned and still-selected account identity; silent only, without claims,
   * forced refresh, interactive fallback or account/cache selection changes.
   */
  getPowerPlatformTokenSilentForCurrentAccount?(expectedTenantId: string): Promise<string>;

  /** Fixed Graph audience, exact current account and expected tenant, silent only.
   * Explicit forceRefresh bypasses the cached access token without adding scopes or sign-in.
   */
  getGraphTokenSilentForCurrentAccount?(
    expectedTenantId: string,
    options?: { forceRefresh?: boolean },
  ): Promise<string>;

  /** Fixed Copilot Studio management audience; silent, selected-user only, no fallback. */
  getCopilotStudioTokenSilent?(): Promise<string>;

  /**
   * Get an access token for the default Power Automate scope
   * Will trigger interactive auth if no cached token available
   */
  getToken(): Promise<string>;

  /**
   * Get an access token for a specific resource
   * @param resource - The resource URL (e.g., "https://service.flow.microsoft.com")
   */
  getTokenForResource(resource: string): Promise<string>;

  /**
   * Redeem a validated Power Platform CAPOLIDS challenge silently, once.
   * No interactive fallback, new scopes, or account change. Optional because
   * alternate authentication providers need not support claims redemption.
   */
  getTokenForResourceWithClaimsSilent?(resource: string, claims: string): Promise<string>;

  /** Snapshot the configured app and selected account for explicit claims redemption. */
  getPowerPlatformClaimsBinding?(): PowerPlatformClaimsBinding | null;

  /**
   * Try to get an access token silently (no interactive prompt).
   * Returns null if silent acquisition fails instead of falling back to device code.
   * Used during setup to probe additional resources without triggering unexpected prompts.
   */
  tryGetTokenForResourceSilent?(resource: string): Promise<string | null>;

  /**
   * Sign out and clear cached tokens
   */
  logout(): Promise<void>;

  /**
   * Get the currently authenticated account info
   */
  getCurrentAccount(): AccountInfo | null;

  /**
   * Check if there's a cached account that can be used for silent auth
   */
  hasAccount(): Promise<boolean>;
}

/**
 * Configuration for WAM authentication
 */
export interface WamAuthConfig {
  /** Azure AD application (client) ID */
  clientId: string;

  /**
   * Azure AD tenant ID
   * - Specific GUID for single-tenant apps
   * - "common" for multi-tenant (default)
   * - "organizations" for any org account
   * - "consumers" for personal Microsoft accounts
   */
  tenantId?: string | null;

  /**
   * Custom path for token cache file
   * Default varies by platform:
   * - Windows: %LOCALAPPDATA%/powerautomate-mcp/token-cache.bin
   * - macOS: ~/Library/Application Support/powerautomate-mcp/token-cache.bin
   * - Linux: ~/.config/powerautomate-mcp/token-cache.bin
   */
  cachePath?: string;
}

/**
 * Token response from MSAL
 */
export interface TokenResponse {
  accessToken: string;
  expiresOn: Date;
  account: AccountInfo;
  scopes: string[];
}

/**
 * Supported Azure AD resources
 */
export const RESOURCES = {
  FLOW_SERVICE: "https://service.flow.microsoft.com",
  POWERAPPS_SERVICE: "https://service.powerapps.com",
  POWERAPPS_API: "https://api.powerapps.com",
  POWER_PLATFORM: "https://api.bap.microsoft.com",
  // Power Pages management API audience. Distinct from POWER_PLATFORM (BAP).
  // Requires the "Power Platform API" delegated permission on the app registration.
  POWER_PLATFORM_API: "https://api.powerplatform.com",
  AZURE_MANAGEMENT: "https://management.azure.com",
  GRAPH: "https://graph.microsoft.com",
} as const;

export type Resource = (typeof RESOURCES)[keyof typeof RESOURCES];

// Microsoft Terraform provider COPILOT_SCOPE. Deliberately NOT a generic URL resource.
export const COPILOT_STUDIO_RESOURCE = "96ff4394-9197-43aa-b393-6a41652e21f8";
